
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { useAuthPayment } from '@/contexts/AuthPaymentContext';

const PaymentAuthComplete: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const { refreshAuthPayment } = useAuthPayment();
  
  // Получаем courier_id из URL параметров
  const courierId = searchParams.get('courier_id');
  
  // Функция для обновления статуса платежа на "completed"
  const updatePaymentStatusByCourierId = async (courier_id: string) => {
    try {
      console.log('Обновление статуса платежа для курьера:', courier_id);
      
      // Запрос к базе данных для получения последнего платежа с типом 'auth' для данного курьера
      const { data, error } = await supabase
        .from('payments')
        .select('id')
        .eq('courier_id', courier_id)
        .eq('status', 'auth')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Ошибка при получении платежа для курьера:', error);
        return { success: false, error };
      }
      
      // Если платеж найден, обновляем его статус
      if (data) {
        const { error: updateError } = await supabase
          .from('payments')
          .update({ status: 'completed' })
          .eq('id', data.id);
        
        if (updateError) {
          console.error('Ошибка при обновлении статуса платежа:', updateError);
          return { success: false, error: updateError };
        }
        
        console.log('Статус платежа успешно обновлен на completed');
        return { success: true };
      }
      
      return { success: true, message: 'Платеж не найден или уже обработан' };
      
    } catch (err) {
      console.error('Ошибка при обновлении статуса платежа:', err);
      return { success: false, error: err };
    }
  };
  
  // Эффект для обновления статуса платежа при загрузке страницы
  useEffect(() => {
    const processPayment = async () => {
      if (!courierId) {
        setStatus('error');
        toast.error('Ошибка: Не указан идентификатор курьера');
        return;
      }
      
      try {
        // Показываем уведомление о проверке статуса
        toast.info('Обновление статуса платежа...');
        
        // Обновляем статус платежа на "completed"
        const result = await updatePaymentStatusByCourierId(courierId);
        
        if (!result.success) {
          console.error('Ошибка обновления статуса:', result.error);
          setStatus('error');
          toast.error('Произошла ошибка при обработке платежа');
          return;
        }
        
        // Помечаем платеж как успешный
        setStatus('success');
        toast.success('Карта успешно авторизована');
        
        // Обновляем состояние контекста авторизационных платежей
        await refreshAuthPayment();
        
        // Автоматический переход на главную страницу через 5 секунд
        setTimeout(() => {
          navigate('/courier');
        }, 5000);
        
      } catch (error) {
        console.error('Ошибка при обработке платежа:', error);
        setStatus('error');
        toast.error('Произошла ошибка при обработке платежа');
      }
    };
    
    processPayment();
  }, [courierId, navigate, refreshAuthPayment]);
  
  // Обработчик для кнопки возврата
  const handleReturnClick = () => {
    navigate('/courier');
  };
  
  // Рендеринг страницы в зависимости от статуса
  return (
    <div className="container max-w-lg mx-auto py-12 px-4">
      <h1 className="text-2xl font-bold mb-6 text-center">Статус авторизации платежа</h1>
      
      {/* Состояние загрузки */}
      {status === 'loading' && (
        <div className="flex flex-col items-center justify-center p-6 bg-background border rounded-lg shadow-sm">
          <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
          <p className="text-lg">Обработка платежа...</p>
          <p className="text-sm text-muted-foreground mt-2">
            Пожалуйста, подождите. Мы обрабатываем вашу авторизацию.
          </p>
        </div>
      )}
      
      {/* Успешный статус */}
      {status === 'success' && (
        <Alert variant="success" className="bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
          <CheckCircle className="h-5 w-5" />
          <AlertTitle>Успешная авторизация</AlertTitle>
          <AlertDescription>
            <p className="mb-2">
              Карта успешно авторизована. Средства не были списаны с карты.
            </p>
            <p className="text-sm mb-4">
              Вы будете автоматически перенаправлены через несколько секунд.
            </p>
            <Button onClick={handleReturnClick} className="mt-2">
              <ArrowLeft className="h-4 w-4 mr-2" /> Вернуться на главную
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Ошибочный статус */}
      {status === 'error' && (
        <Alert variant="destructive">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle>Ошибка авторизации</AlertTitle>
          <AlertDescription>
            <p className="mb-4">
              Произошла ошибка при авторизации карты. Пожалуйста, попробуйте еще раз или используйте другую карту.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={handleReturnClick} variant="default">
                <ArrowLeft className="h-4 w-4 mr-2" /> Вернуться на главную
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default PaymentAuthComplete;
